// Model parity contract: this JS module is the source of truth for deterministic
// driver math. The Flask mirror in backend/Backend-Flask/forecast_driver_model.py
// must match the golden fixtures emitted by forecastDriverModel.test.js.
export const FORECAST_YEARS = [2027, 2028, 2029, 2030, 2031];
export const MONTH_LABELS = ['M01', 'M02', 'M03', 'M04', 'M05', 'M06', 'M07', 'M08', 'M09', 'M10', 'M11', 'M12'];
export const BASE_CREDIT_LOSS_RATE = 0.42;
export const DEFAULT_MACRO_VARIABLES = {
  label: 'Base',
  sofr: 4.85,
  gdp: 2.1,
  inflation: 2.6,
  unemployment: 4.1,
  depositBeta: 38,
  creditLoss: BASE_CREDIT_LOSS_RATE,
  loanDemand: 1,
  prepayment: 8.5,
  depositRunoff: 3.8,
  expenseInflation: 3.1,
  wageInflation: 3.8
};
export const MACRO_SENSITIVITY_COEFFICIENTS = [
  {
    key: 'loanGrowth.gdp',
    coefficient: 0.12,
    formulaKey: 'buildMonthlyProjection.baseAnnualLoanGrowth.gdp',
    description: 'Each GDP point adds 0.12 pts to annual loan-growth before clamps.'
  },
  {
    key: 'depositGrowth.runoff',
    coefficient: -0.08,
    formulaKey: 'buildMonthlyProjection.baseAnnualDepositGrowth.depositRunoff',
    description: 'Runoff above 3.8 reduces deposit growth.'
  },
  {
    key: 'depositCost.runoff',
    coefficient: 0.012,
    formulaKey: 'buildMonthlyProjection.depositCost.depositRunoff',
    description: 'Runoff above 3.8 increases deposit cost.'
  },
  {
    key: 'expense.expenseInflation',
    coefficient: 0.36,
    formulaKey: 'buildMonthlyProjection.nonInterestExpense.expenseInflation',
    description: 'Expense inflation compounds NIE by year index.'
  },
  {
    key: 'provision.creditLoss',
    coefficient: 0.18,
    formulaKey: 'buildMonthlyProjection.creditProvision.creditLoss',
    description: 'Credit loss above base lifts provision.'
  }
];

const finiteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const resolveMacroVariable = (
  variables = {},
  key,
  year,
  defaults = DEFAULT_MACRO_VARIABLES
) => variables?.[key]?.[year] ?? variables?.[key] ?? defaults?.[key];

const materializeMacroVariables = (variables = {}, year = FORECAST_YEARS[0]) => (
  Object.fromEntries(
    Object.keys(DEFAULT_MACRO_VARIABLES).map((key) => [
      key,
      resolveMacroVariable(variables, key, year)
    ])
  )
);

const zeroYearAdjustments = () => ({
  loanGrowthPts: FORECAST_YEARS.map(() => 0),
  depositGrowthPts: FORECAST_YEARS.map(() => 0),
  feeAdjMM: FORECAST_YEARS.map(() => 0),
  expenseAdjMM: FORECAST_YEARS.map(() => 0),
  compAdjMM: FORECAST_YEARS.map(() => 0),
  provisionAdjMM: FORECAST_YEARS.map(() => 0)
});

const normalizeYearAdjustments = (yearAdjustments = {}) => {
  const fallback = zeroYearAdjustments();
  return Object.fromEntries(
    Object.entries(fallback).map(([key, zeros]) => [
      key,
      Array.isArray(yearAdjustments[key])
        ? FORECAST_YEARS.map((_, index) => Number(yearAdjustments[key][index]) || 0)
        : zeros
    ])
  );
};

const loadAdjustmentHelpers = () => {
  // Lazy load avoids a static cycle with forecastAdjustments -> forecastProjection.
  // eslint-disable-next-line no-undef
  return require('../forecastAdjustments');
};

export const periodGrowthPercent = (openingBalance, endingBalance) => {
  const opening = finiteNumber(openingBalance);
  const ending = finiteNumber(endingBalance);
  if (ending === null || opening === null || opening === 0) return null;
  return ((ending / opening) - 1) * 100;
};

export const annualBalanceGrowthPercent = (openingBalance, endingBalance) => {
  const opening = finiteNumber(openingBalance);
  const ending = finiteNumber(endingBalance);
  return opening !== null && opening !== 0 && ending !== null
    ? ((ending / opening) - 1) * 100
    : 0;
};

export const applyMonthlyGrowth = (balance, baseAnnualRate, adjustmentAnnualRate) => (
  Number(balance)
  * (1 + (Number(baseAnnualRate) || 0) / 1200)
  * (1 + (Number(adjustmentAnnualRate) || 0) / 1200)
);

export const combinedAnnualizedGrowthRate = (baseAnnualRate, adjustmentAnnualRate) => (
  ((1 + (Number(baseAnnualRate) || 0) / 1200)
    * (1 + (Number(adjustmentAnnualRate) || 0) / 1200) - 1) * 1200
);

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Projects monthly balances and earnings. `growthAdjustmentAt` is an overlay on
 * the model's intrinsic monthly seasonality, including during a remainder year.
 * @param {{startYear?: number, startMonth?: number, monthCount?: number,
 * growthAdjustmentAt?: (context: {leverId: string, fiscalYear: number,
 * zeroBasedMonth: number}) => number}} options
 */
export const buildMonthlyProjection = (
  model,
  macro,
  label = 'Forecast',
  basePortfolio,
  options = {}
) => {
  let loans = basePortfolio.loans;
  let deposits = basePortfolio.deposits;
  const startYear = Number(options.startYear) || FORECAST_YEARS[0];
  const startMonth = Math.max(1, Math.min(12, Number(options.startMonth) || 1));
  const requestedMonthCount = Number(options.monthCount);
  const monthCount = Number.isFinite(requestedMonthCount)
    ? Math.max(0, requestedMonthCount)
    : 60;

  return Array.from({ length: monthCount }, (_, index) => {
    const monthNumber = index + 1;
    const absoluteMonthIndex = (startMonth - 1) + index;
    const fiscalYear = startYear + Math.floor(absoluteMonthIndex / 12);
    const monthOfYear = absoluteMonthIndex % 12;
    const yearIndex = Math.max(0, fiscalYear - FORECAST_YEARS[0]);
    const yearFade = 1 - Math.min(yearIndex * 0.08, 0.28);
    const seasonality = Math.sin(((monthOfYear + 1) / 12) * Math.PI * 2) * 0.08;
    const gdp = resolveMacroVariable(macro, 'gdp', fiscalYear);
    const depositRunoff = resolveMacroVariable(macro, 'depositRunoff', fiscalYear);
    const expenseInflation = resolveMacroVariable(macro, 'expenseInflation', fiscalYear);
    const creditLoss = resolveMacroVariable(macro, 'creditLoss', fiscalYear);
    const loanGrowthAdjustment = Number(
      options.growthAdjustmentAt?.({
        leverId: 'loanGrowthPts', fiscalYear, zeroBasedMonth: monthOfYear
      })
    ) || 0;
    const depositGrowthAdjustment = Number(
      options.growthAdjustmentAt?.({
        leverId: 'depositGrowthPts', fiscalYear, zeroBasedMonth: monthOfYear
      })
    ) || 0;
    const baseAnnualLoanGrowth = clamp(
      model.adjustedLoanGrowth * yearFade + gdp * 0.12 - yearIndex * 0.18,
      -4,
      9
    );
    const annualLoanGrowth = combinedAnnualizedGrowthRate(
      baseAnnualLoanGrowth + seasonality,
      loanGrowthAdjustment
    );
    const baseAnnualDepositGrowth = clamp(
      model.adjustedDepositGrowth * yearFade
        - Math.max(0, depositRunoff - 3.8) * 0.08
        - yearIndex * 0.12,
      -5,
      9
    );
    const annualDepositGrowth = combinedAnnualizedGrowthRate(
      baseAnnualDepositGrowth - seasonality * 0.6,
      depositGrowthAdjustment
    );
    const previousLoans = loans;
    const previousDeposits = deposits;
    loans = applyMonthlyGrowth(loans, baseAnnualLoanGrowth + seasonality, loanGrowthAdjustment);
    deposits = applyMonthlyGrowth(
      deposits,
      baseAnnualDepositGrowth - seasonality * 0.6,
      depositGrowthAdjustment
    );

    const averageLoans = (previousLoans + loans) / 2;
    const averageDeposits = (previousDeposits + deposits) / 2;
    const loanYield = clamp(model.loanYield + yearIndex * 0.035 + seasonality * 0.02, 3.5, 9.75);
    const depositCost = Math.max(
      basePortfolio.depositRateFloor,
      model.depositCost + yearIndex * 0.015 + Math.max(0, depositRunoff - 3.8) * 0.012
    );
    const interestIncome = averageLoans * (loanYield / 100) / 12;
    const interestExpense = averageDeposits * (depositCost / 100) / 12;
    const fundingBenefit = (model.depositFundingBenefit / 12) * Math.max(0.35, yearFade);
    const nii = interestIncome - interestExpense + fundingBenefit;
    const nonInterestIncome = (model.nonInterestIncome / 12)
      * (1 + yearIndex * 0.016 + monthOfYear * 0.0008);
    const nonInterestExpense = (model.nonInterestExpense / 12)
      * (1 + yearIndex * (expenseInflation / 100) * 0.36);
    const creditProvision = (model.creditProvision / 12)
      * (1 + yearIndex * Math.max(0, creditLoss - BASE_CREDIT_LOSS_RATE) * 0.18);
    const preTaxIncome = nii + nonInterestIncome - nonInterestExpense - creditProvision;
    const taxExpense = Math.max(0, preTaxIncome * (basePortfolio.taxRate / 100));
    const netIncome = preTaxIncome - taxExpense;

    return {
      label,
      monthNumber,
      fiscalYear: `FY${fiscalYear}`,
      fiscalYearNumber: fiscalYear,
      month: MONTH_LABELS[monthOfYear],
      period: `FY${String(fiscalYear).slice(2)} ${MONTH_LABELS[monthOfYear]}`,
      openingLoans: previousLoans,
      openingDeposits: previousDeposits,
      endingLoans: loans,
      endingDeposits: deposits,
      averageLoans,
      averageDeposits,
      loanYield,
      depositCost,
      interestIncome,
      interestExpense,
      nii,
      nonInterestIncome,
      nonInterestExpense,
      creditProvision,
      preTaxIncome,
      taxExpense,
      netIncome,
      nim: averageLoans > 0 ? (nii * 12) / averageLoans * 100 : 0,
      efficiencyRatio: nii + nonInterestIncome > 0
        ? (nonInterestExpense / (nii + nonInterestIncome)) * 100
        : 0,
      annualLoanGrowth,
      annualDepositGrowth
    };
  });
};

export const buildAnnualFromMonthly = (monthlyRows = [], years = FORECAST_YEARS) =>
  years.map((year) => {
    const rows = monthlyRows.filter((row) => row.fiscalYearNumber === year);
    const last = rows[rows.length - 1] || {};
    const totals = rows.reduce(
      (sum, row) => ({
        nii: sum.nii + row.nii,
        nonInterestIncome: sum.nonInterestIncome + row.nonInterestIncome,
        nonInterestExpense: sum.nonInterestExpense + row.nonInterestExpense,
        creditProvision: sum.creditProvision + row.creditProvision,
        taxExpense: sum.taxExpense + row.taxExpense,
        netIncome: sum.netIncome + row.netIncome,
        averageLoans: sum.averageLoans + row.averageLoans,
        averageDeposits: sum.averageDeposits + row.averageDeposits
      }),
      {
        nii: 0,
        nonInterestIncome: 0,
        nonInterestExpense: 0,
        creditProvision: 0,
        taxExpense: 0,
        netIncome: 0,
        averageLoans: 0,
        averageDeposits: 0
      }
    );
    const avgLoans = totals.averageLoans / Math.max(rows.length, 1);
    const openingLoans = Number(rows[0]?.openingLoans);
    const openingDeposits = Number(rows[0]?.openingDeposits);
    const endingLoans = Number(last.endingLoans);
    const endingDeposits = Number(last.endingDeposits);

    return {
      year: `FY${year}`,
      fiscalYearNumber: year,
      endingLoans: Number.isFinite(endingLoans) ? endingLoans : 0,
      endingDeposits: Number.isFinite(endingDeposits) ? endingDeposits : 0,
      averageLoans: avgLoans,
      averageDeposits: totals.averageDeposits / Math.max(rows.length, 1),
      nii: totals.nii,
      nonInterestIncome: totals.nonInterestIncome,
      nonInterestExpense: totals.nonInterestExpense,
      creditProvision: totals.creditProvision,
      taxExpense: totals.taxExpense,
      netIncome: totals.netIncome,
      nim: avgLoans > 0 ? (totals.nii / avgLoans) * 100 : 0,
      efficiencyRatio:
        totals.nii + totals.nonInterestIncome > 0
          ? (totals.nonInterestExpense / (totals.nii + totals.nonInterestIncome)) * 100
          : 0,
      loanGrowth: annualBalanceGrowthPercent(openingLoans, endingLoans),
      depositGrowth: annualBalanceGrowthPercent(openingDeposits, endingDeposits)
    };
  });

export const runDriverModel = ({
  basePortfolio,
  marketInputs = {},
  levers,
  yearAdjustments,
  workforce = {},
  feeDrivers = [],
  shocks = {},
  macroVariables = DEFAULT_MACRO_VARIABLES,
  horizonMonths = 60,
  projectionModel,
  model,
  label = 'Modified plan',
  hireRows = marketInputs.hireRows || workforce.hireRows || [],
  officerTypes = marketInputs.officerTypes || workforce.officerTypes || [],
  typeInputs = marketInputs.typeInputs || marketInputs.officerInputs || marketInputs,
  quarterlyGrowth = marketInputs.quarterlyGrowth || levers?.quarterlyGrowth || null
} = {}) => {
  const { applyYearAdjustments, buildNewProductionMonthly } = loadAdjustmentHelpers();
  const selectedModel = projectionModel || model || marketInputs.projectionModel || marketInputs.model || marketInputs;
  const effectiveYearAdjustments = normalizeYearAdjustments(yearAdjustments || levers?.yearAdjustments || levers);
  const effectiveWorkforce = workforce.yearInputs
    || workforce.workforceYearInputs
    || workforce.years
    || null;
  const workforceCompBase = workforce.compBase
    ?? workforce.existingWorkforceCompBase
    ?? selectedModel.existingWorkforceCompBase
    ?? 0;
  const modelMacroVariables = shocks && Object.keys(shocks).length
    ? {
        ...macroVariables,
        sofr: resolveMacroVariable(macroVariables, 'sofr', FORECAST_YEARS[0]) + (Number(shocks.rateShockBps) || 0) / 100,
        gdp: resolveMacroVariable(macroVariables, 'gdp', FORECAST_YEARS[0]) + (Number(shocks.gdpShock) || 0),
        depositBeta: clamp(
          resolveMacroVariable(macroVariables, 'depositBeta', FORECAST_YEARS[0])
            + (Number(shocks.depositBetaShockPct) || 0),
          5,
          90
        ),
        creditLoss: resolveMacroVariable(macroVariables, 'creditLoss', FORECAST_YEARS[0]) + (Number(shocks.creditStressBps) || 0) / 100,
        depositRunoff: resolveMacroVariable(macroVariables, 'depositRunoff', FORECAST_YEARS[0]) + (Number(shocks.liquidityRunoffPct) || 0),
        expenseInflation: resolveMacroVariable(macroVariables, 'expenseInflation', FORECAST_YEARS[0]) + (Number(shocks.expenseShockPct) || 0),
        wageInflation: resolveMacroVariable(macroVariables, 'wageInflation', FORECAST_YEARS[0]) + (Number(shocks.wageShockPct) || 0)
      }
    : macroVariables;
  const monthlyBase = buildMonthlyProjection(
    selectedModel,
    modelMacroVariables,
    label,
    basePortfolio,
    { monthCount: horizonMonths }
  );
  const newProductionMonthly = buildNewProductionMonthly(
    hireRows,
    officerTypes,
    typeInputs,
    materializeMacroVariables(modelMacroVariables, FORECAST_YEARS[0])
  );
  const monthlySeries = applyYearAdjustments(
    monthlyBase,
    effectiveYearAdjustments,
    feeDrivers,
    newProductionMonthly,
    basePortfolio.taxRate,
    effectiveWorkforce,
    workforceCompBase,
    quarterlyGrowth
  ).adjusted;

  return {
    monthlySeries,
    annualResults: buildAnnualFromMonthly(monthlySeries)
  };
};

export const deriveLatestActualMonth = ({
  isAggregate = false,
  baselineLoading = false,
  latestPeriodDate,
  remainderYear
}) => {
  if (isAggregate || baselineLoading) return 12;
  const match = String(latestPeriodDate || '').match(/^(\d{4})-(\d{2})/);
  if (!match || Number(match[1]) !== Number(remainderYear)) return 12;
  return Math.max(0, Math.min(12, Number(match[2]) || 0));
};

export const deriveBridgeOpeningPortfolio = (series = [], fallbackPortfolio = {}) => {
  const ending = series[series.length - 1];
  if (!ending) return fallbackPortfolio;
  return {
    ...fallbackPortfolio,
    loans: finiteNumber(ending.endingLoans) ?? fallbackPortfolio.loans,
    deposits: finiteNumber(ending.endingDeposits) ?? fallbackPortfolio.deposits
  };
};
